<script setup lang="ts">
  import { ref } from 'vue';

  import type { Invoice, Partner } from '@stuut/api/models';
  import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ui/components/collapsible';
  import { MonetaryText } from '@ui/components/monetary';
  import { Timestamp } from '@ui/components/time';

  import InvoicePromiseToPayPicker from '@/components/common/InvoicePromiseToPayPicker.vue';
  import InvoiceStatusStamp from '@/components/common/InvoiceStatusStamp.vue';
  import { PartnerQuickView } from '@/components/partners';

  defineProps<{
    invoice: Invoice;
    partner?: Partner;
    partnerId?: string;
    partnerName?: string;
  }>();

  const detailsOpen = ref(false);
</script>

<template>
  <div class="bg-card border p-1">
    <div
      class="border-border outline-border text-card-foreground relative w-full overflow-hidden border outline-1 outline-offset-[3px]"
    >
      <div class="flex items-center justify-between px-6 pb-5 pt-5">
        <div class="flex flex-col gap-y-1.5">
          <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
            Invoice
          </p>
          <div class="flex flex-col gap-y-2">
            <p class="text-2xl font-bold leading-none tracking-tight">
              #{{ invoice.number || invoice.id }}
            </p>

            <p v-if="invoice.issued_at" class="text-muted-foreground text-sm">
              Issued {{ invoice.issued_at.local().format('MMM D, YYYY') }}
            </p>
          </div>
        </div>

        <InvoiceStatusStamp
          :invoice="invoice"
          size="lg"
          badge-size="sm"
          stamp-position="right-0 top-1"
        />
      </div>

      <div class="border-border border-t-2 border-dotted" />

      <div class="flex flex-col flex-1 gap-y-1.5 px-6 py-5">
        <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
          Billed to
        </p>

        <div class="flex items-baseline justify-between gap-4">
          <PartnerQuickView
            v-if="partner || partnerId"
            :partner="partner"
            :partner-id="partnerId"
          />

          <strong v-else class="truncate">
            {{ partnerName }}
          </strong>
        </div>
      </div>

      <div class="border-border border-t-2 border-dotted" />

      <div class="divide-border grid grid-cols-2 divide-x divide-dotted">
        <div class="flex flex-col flex-1 gap-y-1.5 px-6 py-5">
          <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
            Due
          </p>

          <p class="text-sm font-medium">
            <template v-if="invoice.dueDateStartOfDay">
              {{ invoice.dueDateStartOfDay.format('MMM D, YYYY') }}
            </template>

            <span v-else class="text-muted-foreground/30">—</span>
          </p>
        </div>

        <div
          v-if="invoice.isPromise || invoice.isBrokenPromise"
          class="flex flex-col flex-1 gap-y-1.5 px-6 py-5"
        >
          <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
            Promised
          </p>

          <InvoicePromiseToPayPicker :invoice="invoice">
            <Timestamp
              class="text-sm font-medium w-fit"
              :class="{
                'bg-destructive/20': invoice.isBrokenPromise,
                'bg-warning/20': invoice.isPromise && !invoice.isBrokenPromise,
              }"
              :time="invoice.promised_to_pay_date"
              format="MMM D, YYYY"
              fallback="Set Date"
              date-only
              side="top"
              editable
              edit-icon-position="right"
            />
          </InvoicePromiseToPayPicker>
        </div>
      </div>

      <div class="border-border border-t-2 border-dotted" />

      <div class="flex flex-col flex-1 gap-y-1.5 px-6 py-5">
        <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
          Balance
        </p>

        <p class="font-mono text-lg font-semibold tabular-nums tracking-tight">
          <MonetaryText
            :value="invoice.pending_amount_remaining"
            :currency="invoice.currency"
            smallest-unit
            :compact="false"
          />

          <span class="text-muted-foreground ml-2 font-mono text-sm font-normal">
            of
            <MonetaryText
              :value="invoice.amount_due"
              :currency="invoice.currency"
              smallest-unit
              :compact="false"
            />
          </span>
        </p>
      </div>

      <Collapsible v-model:open="detailsOpen">
        <CollapsibleContent>
          <div
            v-if="invoice.linked_integration?.integration"
            class="border-border border-t-2 border-dotted"
          />

          <div
            v-if="invoice.linked_integration?.integration"
            class="flex flex-col flex-1 gap-y-1.5 px-6 py-5"
          >
            <p class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
              Source
            </p>

            <p class="text-sm font-medium">
              {{
                invoice.linked_integration.integration.display_name ||
                invoice.linked_integration.integration.name
              }}
            </p>
          </div>

          <div v-if="invoice.line_items?.length" class="border-border border-t border-dotted">
            <table class="w-full text-xs">
              <thead>
                <tr class="border-border border-y border-dotted">
                  <th
                    class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground px-6 py-2.5 text-left"
                  >
                    Description
                  </th>
                  <th
                    class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground px-4 py-2.5 text-right"
                  >
                    Qty × Price
                  </th>
                  <th
                    class="text-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground px-6 py-2.5 text-right"
                  >
                    Total
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="item in invoice.line_items"
                  :key="item.id"
                  class="border-border border-b border-dotted"
                >
                  <td class="px-6 py-2.5">{{ item.description }}</td>
                  <td
                    class="text-muted-foreground whitespace-nowrap px-4 py-2.5 text-right font-mono tabular-nums"
                  >
                    {{ item.quantity }} ×
                    <MonetaryText
                      :value="item.price"
                      :currency="item.currency"
                      smallest-unit
                      :compact="false"
                    />
                  </td>
                  <td class="whitespace-nowrap px-6 py-2.5 text-right font-mono tabular-nums">
                    <MonetaryText
                      :value="item.total_amount"
                      :currency="item.currency"
                      smallest-unit
                      :compact="false"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CollapsibleContent>

        <CollapsibleTrigger
          class="border-border text-muted-foreground hover:text-foreground hover:bg-muted/30 flex w-full items-center justify-center gap-1.5 border-t py-2 text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors"
          :class="detailsOpen && 'border-t-0'"
        >
          <span>Details</span>
          <i
            class="far fa-chevron-down transition-transform"
            :class="detailsOpen && 'rotate-180'"
          />
        </CollapsibleTrigger>
      </Collapsible>
    </div>
  </div>
</template>
